/**
 * The reporter wired to the page: `window` `error` and `unhandledrejection`
 * listeners, the app's `ApiClient` for a report tied to an account, and
 * `sendBeacon` for everything else (plan D48, T69).
 *
 * `early.ts` calls `installErrorReporting()` as the FIRST import of
 * `main.tsx`, so the listeners exist before any other module of the app is
 * evaluated: an error thrown while a module initialises is reported too.
 */

import { clientErrorsUrl } from "@repo/client/data/client-errors";
import { CLIENT_ERRORS_ROUTE, type ClientErrorSource } from "@repo/contract";
import { API_URL, api } from "../../data/api";
import { getPlatform } from "../../native/platform";
import { createReporter, type ReportError } from "./reporter";
import { createSendReport, REPORT_CONTENT_TYPE, type SendMode } from "./send";

let unloading = false;

function sendMode(): SendMode {
  const hidden =
    typeof document !== "undefined" && document.visibilityState === "hidden";
  return unloading || hidden ? "unloading" : "alive";
}

function currentRoute(): string | undefined {
  const { search } = window.location;
  return search === "" ? undefined : search;
}

function platform(): string {
  try {
    return getPlatform();
  } catch {
    return "web";
  }
}

const beacon =
  typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function"
    ? (url: string, body: string) => navigator.sendBeacon(url, body)
    : undefined;

const send = createSendReport({
  authorized: {
    hasSession: () => api.hasSession(),
    post: (body) =>
      api.authorizedFetch(CLIENT_ERRORS_ROUTE, {
        body,
        headers: { "content-type": REPORT_CONTENT_TYPE },
        keepalive: true,
        method: "POST",
      }),
  },
  beacon,
  fetch: (url, init) => fetch(url, init),
  url: clientErrorsUrl(API_URL),
});

/** Report one error. Safe to call from anywhere; never throws. */
export const reportError: ReportError = createReporter({
  bundleVersion: import.meta.env.VITE_BUNDLE_VERSION ?? "dev",
  mode: sendMode,
  now: () => new Date(),
  platform,
  route: currentRoute,
  send,
});

/**
 * What an `error` event carries. `event.error` is null for a cross-origin
 * "Script error." and in some WebViews; the message and position are then
 * all there is.
 */
function thrownOf(event: ErrorEvent): unknown {
  if (event.error !== null && event.error !== undefined) {
    return event.error;
  }
  const where =
    event.filename === ""
      ? undefined
      : `    at ${event.filename}:${event.lineno}:${event.colno}`;
  return { message: event.message || "Script error.", stack: where };
}

function listen(source: ClientErrorSource, thrown: unknown): void {
  reportError(source, thrown);
}

let installed = false;

/** Registers the listeners once; a second call does nothing. */
export function installErrorReporting(): void {
  if (installed || typeof window === "undefined") {
    return;
  }
  installed = true;
  // Not capture: a failed <img> or <script> load fires `error` on the
  // element and does not bubble, so only script errors reach this listener.
  window.addEventListener("error", (event) => {
    listen("error", thrownOf(event));
  });
  window.addEventListener("unhandledrejection", (event) => {
    listen("unhandledrejection", event.reason);
  });
  // A report made while the page goes away has to be a beacon: a fetch
  // started now may be cancelled with the page.
  window.addEventListener("pagehide", () => {
    unloading = true;
  });
  window.addEventListener("pageshow", () => {
    unloading = false;
  });
}
